"use client";
import styled from "@emotion/styled";
import { useTheme } from "@mui/material";
import React from "react";

export interface WcStatusFlowProps {
  steps: {
    label: string;
    value: string;
    failureAlternate?: {
      label: string;
      value: string;
    };
  }[];
  currentStepValue: string;
  stepsMinWidth?: number | string;
  containerMinWidth?: number | string;
}

export const WcStatusFlow = (props: WcStatusFlowProps) => {
  const theme = useTheme();

  const Container = styled.div`
    .container {
      min-width: ${props.containerMinWidth ? props.containerMinWidth : "800px"};
      margin: 0 auto;
      transition: all 0.5s ease;
    }

    .arrow-steps .step {
      font-size: 14px;
      text-align: center;
      color: ${theme.palette.primary.contrastText};
      cursor: default;
      margin: 0 3px;
      padding: 10px 10px 10px 30px;
      min-width: ${props.stepsMinWidth ? props.stepsMinWidth : "200px"};
      float: left;
      position: relative;
      background-color: ${theme.palette.secondary.main};
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      transition: background-color 0.5s ease;
    }

    .arrow-steps .step.error {
      background-color: ${theme.palette.error.dark};
    }

    .arrow-steps .step.error:after {
      border-left: 17px solid ${theme.palette.error.dark};
    }

    .arrow-steps .step.error span:before {
      opacity: 0;
      content: "✖";
      position: absolute;
      top: -2px;
      left: -20px;
    }

    .arrow-steps .step:after,
    .arrow-steps .step:before {
      content: " ";
      position: absolute;
      top: 0;
      right: -17px;
      width: 0;
      height: 0;
      border-top: 19px solid transparent;
      border-bottom: 17px solid transparent;
      border-left: 17px solid ${theme.palette.background.paper};
      z-index: 2;
      transition: border-color 0.5s ease;
    }

    .arrow-steps .step:before {
      right: auto;
      left: 0;
      border-left: 17px solid ${theme.palette.background.paper};
      z-index: 0;
    }

    .arrow-steps .step:first-child:before {
      border: none;
    }

    .arrow-steps .step:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    .arrow-steps .step span {
      position: relative;
    }

    .arrow-steps .step span:before {
      opacity: 0;
      content: "✔";
      position: absolute;
      top: -2px;
      left: -20px;
    }

    .arrow-steps .step.done span:before {
      opacity: 1;
      -webkit-transition: opacity 0.5s ease 0.5s;
      -moz-transition: opacity 0.5s ease 0.5s;
      -ms-transition: opacity 0.5s ease 0.5s;
      transition: opacity 0.5s ease 0.5s;
    }

    .arrow-steps .step.current {
      color: ${theme.palette.primary.contrastText};
      background-color: ${theme.palette.primary.main};
    }

    .arrow-steps .step.current:after {
      border-left: 17px solid ${theme.palette.primary.main};
    }
  `;

  const isAtFailureAlternate = (stepIndex: number): boolean => props.steps[stepIndex].failureAlternate?.value === props.currentStepValue;

  const isAtCurrentStep = (stepIndex: number): boolean => props.steps[stepIndex].value === props.currentStepValue;

  const currentFailureIndex = props.steps.findIndex((step) => step.failureAlternate?.value === props.currentStepValue);

  const currentStepIndex = props.steps.findIndex(
    (step) => step.value === props.currentStepValue || step.value === props.steps[currentFailureIndex]?.value
  );

  const isAtLastStep = currentStepIndex === props.steps.length - 1;

  return (
    <Container>
      <div className="container">
        <div className="arrow-steps">
          {props.steps.map((step, index) => (
            <div
              key={step.value}
              className={`step ${isAtCurrentStep(index) ? "step current" : "step"} ${isAtFailureAlternate(index) ? "error" : ""} ${
                isAtLastStep ? "done" : ""
              }`}
            >
              <span>{isAtFailureAlternate(index) ? step.failureAlternate.label : step.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
};

export default WcStatusFlow;
